import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { of } from 'rxjs';
import { concatMap, finalize, tap } from 'rxjs/operators';
import {
  CardGAEvents,
  DEFAULT_PAGE,
  MAX_ITEMS_PER_PAGE,
} from '@kitch/data-access/constants';
import { Stream, StreamOrderDirection, StreamOrderField, StreamStatus }
  from '@kitch/data-access/models';
import { StreamsSearchParams } from '@kitch/data-access/models/search-params';
import {
  getAllCollaborators,
  getEarthWeekChef,
  getFireChef,
  getIsPaymentRequire,
  getMushroomMonthChef,
  ProfilesService,
  StreamsService,
  TokenService,
} from '@kitch/data-access/services';
import { META_DESCRIPTION_COMING_UP } from '@kitch/util';
import { AfterLoginAction } from '@kitch/ui/directives/need-authorize.directive';
import { SeoService } from '@kitch/ui/services';

@Component({
  selector: 'app-upcoming-streams',
  templateUrl: './upcoming-streams.component.html',
  styleUrls: ['./upcoming-streams.component.scss'],
})
export class UpcomingStreamsComponent implements OnInit, OnDestroy {
  isLoading = false;
  streamId: string;

  upcomingStreams: Stream[];

  profileId: string;
  channelName: string;
  pageTitle: string;
  isPersonalPage: boolean;

  streamBadge: string;

  upcomingStreamsParams: StreamsSearchParams = {
    page: DEFAULT_PAGE,
    itemsPerPage: MAX_ITEMS_PER_PAGE,
    status: StreamStatus.SCHEDULED,
    orderField: StreamOrderField.START_DATE,
    orderDirection: StreamOrderDirection.ASC,
  };

  isPurchaseModalOpened: boolean;
  showPurchaseModal: AfterLoginAction = 'showPurchaseModal';
  purchaseStream: Stream;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private streamsService: StreamsService,
    private $gaService: GoogleAnalyticsService,
    private tokenService: TokenService,
    private profilesService: ProfilesService,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.showPurchaseModalLoggedInGuest();

    this.profileId = this.route.snapshot.params.id || this.tokenService.getProfileId();
    this.isPersonalPage = !!this.route.snapshot.params.id;

    this.getUpcomingStreams();

    this.$gaService.pageView('/scheduled-streams', 'Scheduled streams', undefined, {
      profile_id: this.tokenService.getProfileId(),
    });
  }

  ngOnDestroy() {
    this.seoService.setTagsToDefault();
  }

  setPurchaseModalStatus(status: boolean): void {
    this.isPurchaseModalOpened = status;
  }

  goToLink(stream: Stream): void {
    const streamLink = `/${stream.channel.chefProfile.chefSlug}/streams/${stream.slug}`;

    this.router.navigateByUrl(streamLink).then(() => this.sendGAEvent(stream.title));
  }

  sendGAEvent(streamTitle: string): void {
    this.$gaService.gtag('event', CardGAEvents.upcoming, {
      profile_id: this.tokenService.getProfileId(),
      name: streamTitle,
    });
  }

  private setPageTitle(): void {
    const personalPart = this.isPersonalPage && this.channelName ? ` for ${this.channelName}` : '';

    this.pageTitle = `Coming Up${personalPart}`;
  }

  private showPurchaseModalLoggedInGuest(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.streamsService.showPurchaseModalLoggedInGuest(this.showPurchaseModal)
        .subscribe((res => {
          this.purchaseStream = res;
          if (this.purchaseStream) {
            this.setPurchaseModalStatus(true);
          }
        }));
    }
  }

  private getUpcomingStreams(): void {
    const searchParams = this.isPersonalPage
      ? { ...this.upcomingStreamsParams, profileId: this.profileId }
      : this.upcomingStreamsParams;

    this.streamsService
      .getAll(searchParams)
      .pipe(
        tap((upcomingStreamsParams) => {
          this.upcomingStreams = upcomingStreamsParams.results;
          this.upcomingStreams.forEach((stream) => {
            stream.isPaymentRequire = getIsPaymentRequire(
              stream,
              this.profileId,
              this.tokenService.isAdmin());

            if (stream.collaborators?.length) {
              stream.streamBadge = stream.collaborators.map(collab => collab.badgeImageUrl)[0];
              stream.collaborators = getAllCollaborators(stream.channel.chefProfile, stream.collaborators);
              stream.isMushroomMonth = getMushroomMonthChef(stream) && stream.sponsored;
              stream.isEarthWeek = getEarthWeekChef(stream);
              stream.isFireChef = getFireChef(stream);
            }
          });
        }),
        concatMap(() => {
          if (this.isPersonalPage) {
            return this.profilesService.getUserProfileFullDetail(this.profileId)
              .pipe(tap((res) => (this.channelName = res.profile.channel.name)));
          } else {
            return of({});
          }
        }),
        finalize(() => {
          this.setPageTitle();
          this.isLoading = false;
          this.seoService.updateTags({
            title: this.pageTitle,
            description: META_DESCRIPTION_COMING_UP,
          });
        }),
      )
      .subscribe();
  }
}
